import React, { useState } from "react";
import { trackEvent } from "../utils/analytics";
import "./playerEntryAvatarSelect.scss";

const AVATARS = [
  { id: "avatar_one", src: "./img/fc-avatar-balloon.webp", alt: "Avatar 1" },
  { id: "avatar_two", src: "./img/fc-avatar-bird.webp", alt: "Avatar 2" },
  { id: "avatar_three", src: "./img/fc-avatar-drink.jpg", alt: "Avatar 3" },
  { id: "avatar_four", src: "./img/fc-avatar-flowers.jpg", alt: "Avatar 4" },
  { id: "avatar_five", src: "./img/fc-avatar-helter.jpg", alt: "Avatar 5" },
  { id: "avatar_six", src: "./img/fc-avatar-horse.jpg", alt: "Avatar 6" },
  { id: "avatar_seven", src: "./img/fc-avatar-wheel.jpg", alt: "Avatar 7" },
  { id: "avatar_eight", src: "./img/fc-avatar-dart.jpg", alt: "Avatar 8" },
];

export interface PlayerAvatarSelectProps {
  onAvatarChosen: (avatarSrc: string) => void;
  onBack: () => void;
}

const PAGE_SIZE = 4;
const TOTAL_PAGES = Math.ceil(AVATARS.length / PAGE_SIZE);

export const PlayerAvatarSelect: React.FC<PlayerAvatarSelectProps> = ({ onAvatarChosen, onBack }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [page, setPage] = useState<number>(0);

  const handleConfirm = () => {
    if (selectedIndex !== null) {
      trackEvent({ gaEvent: "avatar_selected" });
      onAvatarChosen(AVATARS[selectedIndex].src);
    }
  };

  const handlePrev = () => {
    if (selectedIndex !== null) {
      // in large view: cycle individual avatars
      setSelectedIndex((prev) => (prev! - 1 + AVATARS.length) % AVATARS.length);
    } else {
      // in grid view: go to previous page
      setPage((p) => (p - 1 + TOTAL_PAGES) % TOTAL_PAGES);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null) {
      // in large view: cycle individual avatars
      setSelectedIndex((prev) => (prev! + 1) % AVATARS.length);
    } else {
      // in grid view: go to next page
      setPage((p) => (p + 1) % TOTAL_PAGES);
    }
  };

  const pageAvatars = AVATARS.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="pageContainer">
      <div className="logoContainer">
        <img src={"./img/fc-logo.svg"} width="125px" alt="Flight Club" />
      </div>
      <div className="contentCard-outer">
        <div className="contentCard">
          <div className="formContainer">
            <div className="formHeader">
              <div className="step">Step 2/3</div>
              <div className="title">Your avatar</div>
            </div>
            <div className="avatarCarousel">
              <button className="avatarNav avatarNav--prev" onClick={handlePrev} aria-label="Previous avatar">
                <img src="./img/avatar-arrow-back.svg" alt="" />
              </button>

              {selectedIndex === null ? (
                <div className="avatarGrid">
                  {pageAvatars.map((avatar, pageIndex) => {
                    const globalIndex = page * PAGE_SIZE + pageIndex;
                    return (
                      <button
                        key={avatar.id}
                        className="avatarOption"
                        onClick={() => setSelectedIndex(globalIndex)}
                        aria-label={avatar.alt}
                      >
                        <img src={avatar.src} alt={avatar.alt} />
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div
                  className="avatarOption avatarOption--selected avatarOption--large"
                  onClick={() => setSelectedIndex(null)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setSelectedIndex(null)}
                  aria-label={AVATARS[selectedIndex].alt}
                >
                  <img src={AVATARS[selectedIndex].src} alt={AVATARS[selectedIndex].alt} />
                </div>
              )}

              <button className="avatarNav avatarNav--next" onClick={handleNext} aria-label="Next avatar">
                <img src="./img/avatar-arrow-forward.svg" alt="" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <footer className="btn-container">
        <button className="small secondary with-back-arrow" onClick={onBack}>
          <img className="previous-arrow" src="./img/previous-arrow.svg" alt="" />
          BACK
        </button>
        <button
          className="small with-forward-arrow"
          onClick={handleConfirm}
          disabled={selectedIndex === null}
        >
          CONFIRM
          <img className="forward-arrow" src="./img/forward-arrow.svg" alt="" />
        </button>
      </footer>
    </div>
  );
};
